import { isSpatiusProvider } from "@/hooks/spatius-primary-session-policy";
import { AvatarSDK, LogLevel as AvatarSdkLogLevel, DrivingServiceMode } from "@spatius/avatarkit";
import { useEffect, useRef } from "react";

/**
 * Eagerly initializes the Spatius AvatarSDK singleton on page load so
 * that when a session starts the `ensureAvatarSdk()` call inside
 * `useSpatiusAvatar` becomes a no-op (~100-300ms saved).
 *
 * This hook is intentionally best-effort: if pre-init fails the session-time
 * init in `useSpatiusAvatar` will still work as the authoritative path.
 *
 * **Cost: zero.** `AvatarSDK.initialize()` is a pure client-side singleton
 * setup - no API calls, no session tokens consumed, no billing impact.
 */
export function useSpatiusSdkPreinit(enabled = true): void {
  const attemptedRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    if (attemptedRef.current) {
      return;
    }

    const provider = (process.env.NEXT_PUBLIC_AVATAR_PROVIDER || "anam").trim().toLowerCase();
    const appId = process.env.NEXT_PUBLIC_SPATIUS_APP_ID;

    if (!isSpatiusProvider(provider) || !appId) {
      return;
    }

    attemptedRef.current = true;

    if ((AvatarSDK as typeof AvatarSDK & { isInitialized?: boolean }).isInitialized) {
      return;
    }

    AvatarSDK.initialize(appId, {
      drivingServiceMode: DrivingServiceMode.rtc,
      logLevel:
        process.env.NEXT_PUBLIC_ENABLE_RTC_SDK_LOGS === "1" ? undefined : AvatarSdkLogLevel.off,
      region: process.env.NEXT_PUBLIC_SPATIUS_REGION || "us-west",
    } as Parameters<typeof AvatarSDK.initialize>[1] & { region?: string }).catch(() => {
      // Silently swallow - session-time init is the authoritative fallback.
    });
  }, [enabled]);
}
